import React, { useState } from 'react';
import { X, Copy, Check } from 'lucide-react';

const WidgetEmbedModal = ({ isOpen, onClose, apiKey }) => {
  const [copied, setCopied] = useState(false);

  // Use the API base URL for widget embed
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';

  const embedCode = `<script
  src="${apiBaseUrl}/widget/embed.js"
  data-api-key="${apiKey}"
  async
></script>`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full p-6 z-10">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Install Widget</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="space-y-6">
            {/* Instructions */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Installation Instructions
              </h3>
              <p className="text-gray-600 mb-4">
                Copy the code below and paste it before the closing <code className="bg-gray-100 px-2 py-1 rounded text-sm">&lt;/body&gt;</code> tag on your website.
              </p>
            </div>

            {/* Embed Code */}
            <div>
              <div className="bg-gray-900 rounded-lg p-4 relative">
                <pre className="text-gray-100 text-sm overflow-x-auto">
                  <code>{embedCode}</code>
                </pre>

                {/* Copy Button */}
                <button
                  onClick={handleCopy}
                  className="absolute top-3 right-3 bg-gray-800 hover:bg-gray-700 text-white px-3 py-2 rounded-md flex items-center gap-2 transition-colors"
                >
                  {copied ? (
                    <>
                      <Check size={16} />
                      <span className="text-sm">Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy size={16} />
                      <span className="text-sm">Copy</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* API Key Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-900 mb-1">
                Your API Key
              </h4>
              <p className="text-sm text-blue-700 font-mono break-all">
                {apiKey}
              </p>
            </div>

            {/* Additional Info */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-gray-900">
                What happens next?
              </h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">•</span>
                  <span>A chat button will appear on your website</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">•</span>
                  <span>Visitors can submit messages through the widget</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">•</span>
                  <span>Messages appear as tickets in your dashboard</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">•</span>
                  <span>Customize appearance and behavior in widget settings</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end items-center mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WidgetEmbedModal;
